/**
 * useAuth — авторизация и профиль (Личный кабинет, ЧТЗ-AUTH-002 v2.0).
 *
 * Access-токен хранится в localStorage (ключ `catrus_token`).
 * Refresh-токен — httpOnly cookie, выставляется бэкендом (вне доступа JS).
 */
import { useState, useEffect, useCallback } from 'react';
import { API_URL } from '../utils/config.js';

const TOKEN_KEY = 'catrus_token';

/** Standalone-геттер токена — для использования вне React-компонентов. */
export function getToken() {
  return localStorage.getItem(TOKEN_KEY);
}

/**
 * Обработчик 401, регистрируемый хуком useAuth (clearSession).
 * Позволяет plain-модулям (catalog/api.js) централизованно сбрасывать
 * сессию при протухшем токене, не вызывая хук напрямую (FR-AUTH-29).
 */
let unauthorizedHandler = null;
export function setUnauthorizedHandler(fn) { unauthorizedHandler = fn; }

/**
 * Standalone fetch с Bearer-токеном — для использования вне React (data-запросы).
 * Прикрепляет Authorization при наличии токена; при 401 дёргает зарегистрированный
 * обработчик (clearSession) — централизованная обработка 401 (FR-AUTH-28, FR-AUTH-29).
 */
export async function authedFetch(path, opts = {}) {
  const token = getToken();
  const headers = { ...(opts.headers || {}) };
  if (token) headers.Authorization = `Bearer ${token}`;
  if (opts.body && !headers['Content-Type']) {
    headers['Content-Type'] = 'application/json';
  }
  const resp = await fetch(`${API_URL}${path}`, { ...opts, headers });
  if (resp.status === 401 && unauthorizedHandler) unauthorizedHandler();
  return resp;
}

/**
 * Извлекает строковый код ошибки из тела ответа FastAPI.
 * Для 401/403 `detail` — строка-код; для 422 `detail` — массив ошибок Pydantic.
 * Возвращает строку-код или 'VALIDATION_ERROR' для массива.
 */
export function parseDetail(body, status) {
  if (status === 422 || Array.isArray(body?.detail)) return 'VALIDATION_ERROR';
  if (typeof body?.detail === 'string') return body.detail;
  return null;
}

/** Безопасно читает JSON-тело ответа (тело может отсутствовать). */
async function safeJson(resp) {
  try {
    return await resp.json();
  } catch {
    return null;
  }
}

export function useAuth() {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(false);
  // Начальная проверка токена завершена (FR-AUTH-24) — чтобы гейт не мигал логином.
  const [initialized, setInitialized] = useState(false);

  const isAuthenticated = !!currentUser;

  /** Очистка локальной сессии (токен + профиль). */
  const clearSession = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setCurrentUser(null);
  }, []);

  /**
   * fetch с автоматической подстановкой Bearer-токена.
   * Делегирует в module-level authedFetch — единая точка обработки 401 (clearSession).
   */
  const authFetch = useCallback((path, opts = {}) => authedFetch(path, opts), []);

  /** Загрузка профиля текущего пользователя — GET /users/me. */
  const fetchProfile = useCallback(async () => {
    const token = getToken();
    if (!token) {
      setCurrentUser(null);
      return null;
    }
    setLoading(true);
    try {
      const resp = await authFetch('/users/me');
      if (!resp.ok) {
        setCurrentUser(null);
        return null;
      }
      const data = await resp.json();
      setCurrentUser(data);
      return data;
    } catch {
      setCurrentUser(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, [authFetch]);

  /** Вход по email/паролю — POST /auth/login. */
  const login = useCallback(async (email, password) => {
    let resp;
    try {
      resp = await fetch(`${API_URL}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, password }),
      });
    } catch {
      const error = new Error('SERVICE_UNAVAILABLE');
      error.code = 'SERVICE_UNAVAILABLE';
      throw error;
    }
    if (!resp.ok) {
      if (resp.status >= 500) {
        const error = new Error('SERVICE_UNAVAILABLE');
        error.code = 'SERVICE_UNAVAILABLE';
        throw error;
      }
      const body = await safeJson(resp);
      const code = parseDetail(body, resp.status) || 'LOGIN_FAILED';
      const error = new Error(code);
      error.code = code;
      throw error;
    }
    const data = await resp.json();
    localStorage.setItem(TOKEN_KEY, data.access_token);
    await fetchProfile();
    return data;
  }, [fetchProfile]);

  /** Регистрация — POST /auth/register. Бросает Error с .code при ошибке. */
  const register = useCallback(async (payload) => {
    let resp;
    try {
      resp = await fetch(`${API_URL}/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
    } catch {
      const error = new Error('SERVICE_UNAVAILABLE');
      error.code = 'SERVICE_UNAVAILABLE';
      throw error;
    }
    if (!resp.ok) {
      if (resp.status >= 500) {
        const error = new Error('SERVICE_UNAVAILABLE');
        error.code = 'SERVICE_UNAVAILABLE';
        throw error;
      }
      const body = await safeJson(resp);
      const code = parseDetail(body, resp.status) || 'REGISTER_FAILED';
      const error = new Error(code);
      error.code = code;
      throw error;
    }
    return safeJson(resp);
  }, []);

  /**
   * Запрос восстановления пароля — POST /auth/forgot-password.
   * Backend всегда отвечает 200 (anti-enumeration). Сетевые/5xx ошибки бросаются.
   */
  const forgotPassword = useCallback(async (email, recaptchaToken = null) => {
    let resp;
    try {
      resp = await fetch(`${API_URL}/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, recaptcha_token: recaptchaToken }),
      });
    } catch {
      const error = new Error('SERVICE_UNAVAILABLE');
      error.code = 'SERVICE_UNAVAILABLE';
      throw error;
    }
    if (resp.status >= 500) {
      const error = new Error('SERVICE_UNAVAILABLE');
      error.code = 'SERVICE_UNAVAILABLE';
      throw error;
    }
    return true;
  }, []);

  /** Сброс пароля по токену — POST /auth/reset-password. */
  const resetPassword = useCallback(async (token, newPassword) => {
    let resp;
    try {
      resp = await fetch(`${API_URL}/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, new_password: newPassword }),
      });
    } catch {
      const error = new Error('SERVICE_UNAVAILABLE');
      error.code = 'SERVICE_UNAVAILABLE';
      throw error;
    }
    if (!resp.ok) {
      if (resp.status >= 500) {
        const error = new Error('SERVICE_UNAVAILABLE');
        error.code = 'SERVICE_UNAVAILABLE';
        throw error;
      }
      const body = await safeJson(resp);
      const code = parseDetail(body, resp.status) || 'RESET_FAILED';
      const error = new Error(code);
      error.code = code;
      throw error;
    }
    return true;
  }, []);

  /**
   * Подтверждение email по токену из письма — POST /auth/verify-email.
   * Ссылка письма ведёт на фронтовый /verify?token=… (BUG-AUTH-01).
   * Бросает Error с .code (INVALID_TOKEN | TOKEN_USED | TOKEN_EXPIRED | SERVICE_UNAVAILABLE).
   */
  const verifyEmail = useCallback(async (token) => {
    let resp;
    try {
      resp = await fetch(`${API_URL}/auth/verify-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      });
    } catch {
      const error = new Error('SERVICE_UNAVAILABLE');
      error.code = 'SERVICE_UNAVAILABLE';
      throw error;
    }
    if (!resp.ok) {
      if (resp.status >= 500) {
        const error = new Error('SERVICE_UNAVAILABLE');
        error.code = 'SERVICE_UNAVAILABLE';
        throw error;
      }
      const body = await safeJson(resp);
      const code = parseDetail(body, resp.status) || 'INVALID_TOKEN';
      const error = new Error(code);
      error.code = code;
      throw error;
    }
    return true;
  }, []);

  /** Выход — POST /auth/logout + локальная очистка. */
  const logout = useCallback(async () => {
    try {
      await authFetch('/auth/logout', { method: 'POST', credentials: 'include' });
    } catch { /* всё равно чистим локально */ }
    clearSession();
  }, [authFetch, clearSession]);

  // Регистрируем централизованный обработчик 401 для module-level authedFetch (FR-AUTH-29).
  useEffect(() => {
    setUnauthorizedHandler(clearSession);
    return () => setUnauthorizedHandler(null);
  }, [clearSession]);

  // При монтировании — подтягиваем профиль, если токен есть.
  // По завершении (с токеном или без) выставляем initialized — гейт перестаёт показывать splash (FR-AUTH-24).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (getToken()) await fetchProfile();
      if (!cancelled) setInitialized(true);
    })();
    return () => { cancelled = true; };
  }, [fetchProfile]);

  return {
    currentUser,
    isAuthenticated,
    loading,
    initialized,
    login,
    register,
    forgotPassword,
    resetPassword,
    verifyEmail,
    logout,
    fetchProfile,
    authFetch,
    clearSession,
  };
}
